<header
  [class]="'s__header s__bg-dark' + (mobileOpen() ? ' s__header--mobile-open' : '')"
>
  <div class="s__container s__header__inner">
    <!-- Logo -->
    <a href="/" class="s__header__logo" aria-label="Samotics - Home">
      <img src="samotics-logo-white.png" alt="Samotics" class="s__header__logo-img" width="812" height="161" decoding="async" loading="eager">
    </a>

    <!-- Desktop Navigation -->
    <nav class="s__header__nav" aria-label="Main navigation">
      @for (item of navItems(); track item.label) {
        @if (hasDropdown(item)) {
          <div
            class="s__header__nav-group"
            (mouseenter)="openMenu(item.label)"
            (mouseleave)="closeMenu()"
          >
            <button
              class="s__header__nav-trigger"
              [class.s__header__nav-trigger--active]="activeMenu() === item.label"
              [attr.aria-expanded]="activeMenu() === item.label"
              aria-haspopup="true"
              [attr.aria-controls]="'mega-' + item.label"
              (click)="toggleMenu(item.label)"
              (keydown)="onKeydown($event, item.label)"
            >
              {{ item.label }}
              <svg class="s__header__chevron" width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
                <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <div
              class="s__header__mega-panel"
              [class.s__header__mega-panel--active]="activeMenu() === item.label"
              [id]="'mega-' + item.label"
              (mouseenter)="cancelClose()"
              (mouseleave)="closeMenu()"
            >
              <div class="s__container">
                <div class="s__header__mega-grid">
                  @if (item.groups && item.groups.length > 0) {
                    @for (group of item.groups; track group.groupLabel) {
                      <div class="s__header__mega-group">
                        <span class="s__header__mega-group-label">{{ group.groupLabel }}</span>
                        @for (link of group.items; track link.href) {
                          <a [href]="link.href"
                            [attr.target]="isExternal(link.href) ? '_blank' : null"
                            [attr.rel]="isExternal(link.href) ? 'noopener' : null"
                            class="s__header__mega-link"
                            (click)="handleMegaLinkClick(item.label)">
                            <span class="s__header__mega-link-title">{{ link.title }}</span>
                            <span class="s__header__mega-link-desc">{{ link.description }}</span>
                          </a>
                        }
                      </div>
                    }
                  } @else {
                    @for (link of item.links; track link.href) {
                      <a [href]="link.href"
                        [attr.target]="isExternal(link.href) ? '_blank' : null"
                        [attr.rel]="isExternal(link.href) ? 'noopener' : null"
                        class="s__header__mega-link"
                        (click)="handleMegaLinkClick(item.label)">
                        <span class="s__header__mega-link-title">{{ link.title }}</span>
                        <span class="s__header__mega-link-desc">{{ link.description }}</span>
                      </a>
                    }
                  }
                </div>
              </div>
            </div>
          </div>
        } @else {
          @if (item.href) {
            <a [href]="item.href"
              [attr.target]="isExternal(item.href) ? '_blank' : null"
              [attr.rel]="isExternal(item.href) ? 'noopener' : null"
              class="s__header__nav-trigger s__header__nav-trigger--link">
              {{ item.label }}
            </a>
          } @else {
            <span class="s__header__nav-trigger s__header__nav-trigger--link s__header__nav-trigger--disabled">
              {{ item.label }}
            </span>
          }
        }
      }
    </nav>

    @if (navigation(); as n) {
      <!-- Desktop CTAs -->
      <div class="s__header__actions">
        @if (n.secondaryCtaHref) {
          <a [href]="n.secondaryCtaHref">
            <ds-button variant="outline">{{ n.secondaryCtaLabel }}</ds-button>
          </a>
        } @else {
          <ds-button variant="outline" [disabled]="true">{{ n.secondaryCtaLabel }}</ds-button>
        }
        @if (n.ctaButtonHref) {
          <a [href]="n.ctaButtonHref">
            <ds-button variant="primary">{{ n.ctaButtonLabel }}</ds-button>
          </a>
        } @else {
          <ds-button variant="primary" [disabled]="true">{{ n.ctaButtonLabel }}</ds-button>
        }
      </div>

      <!-- Mobile CTAs -->
      <div class="s__header__mobile-ctas">
        @if (n.secondaryCtaHref) {
          <a [href]="n.secondaryCtaHref">
            <ds-button variant="outline">{{ n.secondaryCtaLabel }}</ds-button>
          </a>
        } @else {
          <ds-button variant="outline" [disabled]="true">{{ n.secondaryCtaLabel }}</ds-button>
        }
        @if (n.ctaButtonHref) {
          <a [href]="n.ctaButtonHref">
            <ds-button variant="primary">{{ n.ctaButtonLabel }}</ds-button>
          </a>
        } @else {
          <ds-button variant="primary" [disabled]="true">{{ n.ctaButtonLabel }}</ds-button>
        }
      </div>
    }

    <!-- Mobile Hamburger -->
    <button
      class="s__header__hamburger"
      [attr.aria-label]="mobileOpen() ? 'Close menu' : 'Open menu'"
      (click)="toggleMobile()"
    >
      @if (mobileOpen()) {
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6 6L18 18M6 18L18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      } @else {
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M3 6H21M3 12H21M3 18H21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
      }
    </button>
  </div>

  <!-- Mobile Panel -->
  <div class="s__header__mobile-panel">
    <nav aria-label="Mobile navigation">
      @for (item of navItems(); track item.label) {
        @if (hasDropdown(item)) {
          <div class="s__header__mobile-section">
            <button
              class="s__header__mobile-trigger"
              [class.s__header__mobile-trigger--expanded]="isMobileSectionExpanded(item.label)"
              [attr.aria-expanded]="isMobileSectionExpanded(item.label)"
              (click)="toggleMobileSection(item.label)"
            >
              {{ item.label }}
              <svg class="s__header__mobile-chevron" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
                <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <div
              class="s__header__mobile-links"
              [class.s__header__mobile-links--expanded]="isMobileSectionExpanded(item.label)"
            >
              <div class="s__header__mobile-links-inner">
                @if (item.groups && item.groups.length > 0) {
                  @for (group of item.groups; track group.groupLabel) {
                    <span class="s__header__mobile-group-label">{{ group.groupLabel }}</span>
                    @for (link of group.items; track link.href) {
                      <a [href]="link.href"
                        [attr.target]="isExternal(link.href) ? '_blank' : null"
                        [attr.rel]="isExternal(link.href) ? 'noopener' : null"
                        class="s__header__mobile-link"
                        (click)="handleMobileLinkClick()">{{ link.title }}</a>
                    }
                  }
                } @else {
                  @for (link of item.links; track link.href) {
                    <a [href]="link.href"
                      [attr.target]="isExternal(link.href) ? '_blank' : null"
                      [attr.rel]="isExternal(link.href) ? 'noopener' : null"
                      class="s__header__mobile-link"
                      (click)="handleMobileLinkClick()">{{ link.title }}</a>
                  }
                }
              </div>
            </div>
          </div>
        } @else {
          @if (item.href) {
            <a [href]="item.href"
              [attr.target]="isExternal(item.href) ? '_blank' : null"
              [attr.rel]="isExternal(item.href) ? 'noopener' : null"
              class="s__header__mobile-direct-link"
              (click)="handleMobileLinkClick()">
              {{ item.label }}
            </a>
          } @else {
            <span class="s__header__mobile-direct-link s__header__mobile-direct-link--disabled">
              {{ item.label }}
            </span>
          }
        }
      }
    </nav>

  </div>
</header>
